import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getHeatLabel } from '@/hooks/useHeatCounter';
import { createSegmentFilter } from '@/lib/gpsFilter';
import { ArrowRight } from 'lucide-react';

// Brand Colors
const BRAND = {
  black: '#000000',
  darkPanel: '#1C1C1E',
  darkRow: '#2C2C2E',
  red: '#FF3B30',
  orange: '#FF9500',
  white: '#FFFFFF',
  lightGray: '#CCCCCC',
  mediumGray: '#AAAAAA',
  gridGray: '#333333'
};

// Format duration from seconds to mm:ss
const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Format distance
const formatDistance = (meters) => {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
};

// Format speed
const formatSpeed = (ms) => {
  return ms.toFixed(2);
};

// Determine split interval based on total session distance (+100m leeway per tier)
// 0-600m: 50m | 601-1100m: 100m | 1101-2100m: 200m | 2101-3100m: 300m | +100m pattern continues
const getSplitInterval = (totalDistance) => {
  if (totalDistance <= 600) return 50;
  return 100 * Math.ceil((totalDistance - 100) / 1000);
};

// Format split time (cumulative elapsed time)
const formatSplitTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  
  if (hours >= 1) {
    // hh:mm:ss format for 1 hour or longer
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  // mm:ss format for under 1 hour
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Calculate split speeds from coordinates (exported for testing)
export const calculateSplitSpeeds = (coordinates, targetDistance, totalDistance) => {
  if (!coordinates || coordinates.length < 2) {
    return [];
  }

  const splits = [];
  
  // GPS warm-up: skip first 3 readings (phone reports stale cached position at startup)
  const GPS_WARMUP = 3;
  
  // Calculate distance between two GPS coordinates (Haversine formula)
  const calcDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // First pass: build filtered distance/time pairs (same shared filter as live tracker)
  const acceptSegment = createSegmentFilter();
  const filteredPoints = []; // { cumulativeDistance, timestamp }
  let cumulativeDistance = 0;
  let startIdx = Math.min(GPS_WARMUP, coordinates.length - 1);

  filteredPoints.push({ 
    cumulativeDistance: 0, 
    timestamp: coordinates[startIdx].timestamp 
  });

  for (let i = startIdx + 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];
    
    const segmentDistance = calcDistance(
      prev.latitude, prev.longitude,
      curr.latitude, curr.longitude
    );
    
    // Apply same shared filter as live tracker: accuracy, distance bounds,
    // absolute speed cap, and adaptive spike rejection
    const prevTime = new Date(prev.timestamp).getTime();
    const currTime = new Date(curr.timestamp).getTime();
    const timeDiff = (currTime - prevTime) / 1000;
    const accuracy = curr.accuracy || 0;
    
    if (acceptSegment(segmentDistance, timeDiff, accuracy)) {
      cumulativeDistance += segmentDistance;
      filteredPoints.push({ 
        cumulativeDistance, 
        timestamp: curr.timestamp 
      });
    }
  }

  // Split interval is based on the actual filtered session distance
  const interval = getSplitInterval(cumulativeDistance);

  // Second pass: calculate splits from filtered points
  let splitStartIdx = 0;
  let splitStartDistance = 0;
  let currentSplitEnd = interval;
  
  // Get session start time for cumulative elapsed time calculation
  const sessionStartTime = new Date(filteredPoints[0].timestamp).getTime();

  for (let i = 1; i < filteredPoints.length; i++) {
    while (filteredPoints[i].cumulativeDistance >= currentSplitEnd && currentSplitEnd <= cumulativeDistance) {
      const splitDistance = currentSplitEnd - splitStartDistance;
      const startTime = new Date(filteredPoints[splitStartIdx].timestamp).getTime();
      const endTime = new Date(filteredPoints[i].timestamp).getTime();
      const splitTime = (endTime - startTime) / 1000;
      
      // Calculate cumulative elapsed time from session start
      const cumulativeElapsedTime = (endTime - sessionStartTime) / 1000;
      
      const splitSpeed = splitTime > 0 ? splitDistance / splitTime : 0;
      
      splits.push({
        start: currentSplitEnd - interval,
        end: currentSplitEnd,
        speed: splitSpeed,
        label: `${currentSplitEnd - interval}m - ${currentSplitEnd}m`,
        elapsedTime: cumulativeElapsedTime
      });

      splitStartIdx = i;
      splitStartDistance = currentSplitEnd;
      currentSplitEnd += interval;
    }
  }

  // Add final partial split
  if (cumulativeDistance > splitStartDistance && filteredPoints.length > 1) {
    const startTime = new Date(filteredPoints[splitStartIdx].timestamp).getTime();
    const endTime = new Date(filteredPoints[filteredPoints.length - 1].timestamp).getTime();
    const splitTime = (endTime - startTime) / 1000;
    const splitDistance = cumulativeDistance - splitStartDistance;
    
    // Calculate cumulative elapsed time from session start
    const cumulativeElapsedTime = (endTime - sessionStartTime) / 1000;
    
    if (splitTime > 0 && splitDistance > 5) {
      splits.push({
        start: Math.round(splitStartDistance),
        end: Math.round(cumulativeDistance),
        speed: splitDistance / splitTime,
        label: `${Math.round(splitStartDistance)}m - ${Math.round(cumulativeDistance)}m`,
        elapsedTime: cumulativeElapsedTime
      });
    }
  }

  return splits;
};

// Simple Line Chart Component
const SpeedChart = ({ splits }) => {
  if (!splits || splits.length === 0) return null;

  const maxSpeed = Math.max(...splits.map(s => s.speed), 1);
  const minSpeed = Math.min(...splits.map(s => s.speed), 0);
  const speedRange = maxSpeed - minSpeed || 1;
  
  const chartHeight = 150;
  const chartWidth = 100; // percentage
  const padding = { top: 20, bottom: 30, left: 40, right: 20 };
  
  // Generate points for the line
  const points = splits.map((split, index) => {
    const x = padding.left + (index / (splits.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
    const y = padding.top + (1 - (split.speed - minSpeed) / speedRange) * (chartHeight - padding.top - padding.bottom);
    return { x: `${x}%`, y, speed: split.speed, distance: split.end };
  });

  return (
    <div className="relative w-full" style={{ height: chartHeight }}>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 h-full flex flex-col justify-between py-5 text-xs" style={{ color: BRAND.mediumGray }}>
        <span>{maxSpeed.toFixed(1)}</span>
        <span>{((maxSpeed + minSpeed) / 2).toFixed(1)}</span>
        <span>{minSpeed.toFixed(1)}</span>
      </div>
      
      {/* Grid lines */}
      <svg className="absolute inset-0 w-full h-full" style={{ left: '10%' }}>
        {[0, 1, 2].map((i) => (
          <line
            key={i}
            x1="0"
            y1={`${padding.top + (i / 2) * (chartHeight - padding.top - padding.bottom)}px`}
            x2="90%"
            y2={`${padding.top + (i / 2) * (chartHeight - padding.top - padding.bottom)}px`}
            stroke={BRAND.gridGray}
            strokeWidth="1"
          />
        ))}
      </svg>
      
      <svg className="absolute inset-0 w-full h-full overflow-visible">
        {/* Data points */}
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="5"
            fill={BRAND.orange}
            stroke={BRAND.black}
            strokeWidth="2"
          />
        ))}
      </svg>
      
      {/* X-axis labels */}
      <div 
        className="absolute bottom-0 left-10 right-0 flex justify-between text-xs px-2"
        style={{ color: BRAND.mediumGray }}
      >
        {splits.filter((_, i) => i === 0 || i === splits.length - 1 || i === Math.floor(splits.length / 2)).map((split, index) => (
          <span key={index}>{split.end}m</span>
        ))}
      </div>
    </div>
  );
};

const SessionSummaryPage_DBPRO = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get session data from navigation state or generate demo data
  const rawSessionData = location.state || {};
  
  // Check if we have real data, otherwise use demo data for display
  const hasRealData = rawSessionData.totalDistance > 0;
  
  const sessionData = hasRealData ? rawSessionData : {
    totalDistance: 487.3,
    duration: 156,
    averageSpeed: 3.12,
    maxSpeed: 4.1,
    targetDistance: 500,
    mode: 'race',
    coordinates: [],
    autoStopped: true,
    // Preserve heatNumber from navigation state even when using demo data
    heatNumber: rawSessionData.heatNumber || 0
  };
  
  const {
    totalDistance = 0,
    duration = 0,
    averageSpeed = 0,
    maxSpeed = 0,
    targetDistance = 500,
    mode = 'practice',
    coordinates = [],
    autoStopped = false,
    heatNumber = 0
  } = sessionData;

  // Calculate split speeds
  const splits = useMemo(() => {
    return calculateSplitSpeeds(coordinates, targetDistance, totalDistance);
  }, [coordinates, targetDistance, totalDistance]);

  const handleViewHistory = () => {
    navigate('/history');
  };

  const handleNewSession = () => {
    navigate('/dashboard');
  };

  return (
    <div 
      className="min-h-screen flex flex-col px-4 py-6"
      style={{ backgroundColor: BRAND.black }}
      data-testid="summary-page"
    >
      {/* Title */}
      <h1 
        className="text-center font-bold mb-2"
        style={{ color: BRAND.white, fontSize: '22px' }}
        data-testid="summary-title"
      >
        Session Summary
      </h1>
      
      {/* Subtitle */}
      <p 
        className="text-center mb-6"
        style={{ color: BRAND.lightGray, fontSize: '16px' }}
        data-testid="summary-subtitle"
      >
        Here is how your last session went.
      </p>

      {/* Auto-stop indicator */}
      {autoStopped && (
        <div 
          className="text-center mb-4 py-2 px-4 rounded-lg mx-auto"
          style={{ backgroundColor: BRAND.darkPanel, color: BRAND.orange }}
          data-testid="auto-stop-badge"
        >
          🎯 Auto-stopped at target distance
        </div>
      )}

      {/* Metrics Section */}
      <div 
        className="rounded-xl p-4 mb-6"
        style={{ backgroundColor: BRAND.darkPanel }}
        data-testid="metrics-section"
      >
        <div className="grid grid-cols-3 gap-4 text-center">
          {/* Total Distance */}
          <div data-testid="metric-total-distance">
            <p 
              className="font-bold mb-1"
              style={{ color: BRAND.red, fontSize: '20px' }}
            >
              {formatDistance(totalDistance)}
            </p>
            <p style={{ color: BRAND.mediumGray, fontSize: '14px' }}>
              Total Distance
            </p>
          </div>
          
          {/* Total Time */}
          <div data-testid="metric-total-time">
            <p 
              className="font-bold mb-1"
              style={{ color: BRAND.red, fontSize: '20px' }}
            >
              {formatDuration(duration)}
            </p>
            <p style={{ color: BRAND.mediumGray, fontSize: '14px' }}>
              Total Time
            </p>
          </div>
          
          {/* Average Speed */}
          <div data-testid="metric-avg-speed">
            <p 
              className="font-bold mb-1"
              style={{ color: BRAND.red, fontSize: '20px' }}
            >
              {formatSpeed(averageSpeed)} m/s
            </p>
            <p style={{ color: BRAND.mediumGray, fontSize: '11px', marginTop: '-2px' }}>
              ≈ {(averageSpeed * 3.6).toFixed(1)} km/h
            </p>
            <p style={{ color: BRAND.mediumGray, fontSize: '14px', marginTop: '4px' }}>
              Avg Speed
            </p>
          </div>
        </div>
      </div>

      {/* Split Speed Section */}
      <div className="flex-1 mb-6">
        <h2 
          className="font-bold mb-4 text-left"
          style={{ color: BRAND.white, fontSize: '18px' }}
          data-testid="split-section-title"
        >
          Speed Performance by Split{heatNumber > 0 ? ` — ${getHeatLabel(heatNumber)}` : ''}
        </h2>
        
        {/* Line Chart */}
        {splits.length > 0 && (
          <div 
            className="rounded-xl p-4 mb-4"
            style={{ backgroundColor: BRAND.darkPanel }}
            data-testid="speed-chart"
          >
            <SpeedChart splits={splits} />
          </div>
        )}
        
        {/* Split Table */}
        <div 
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: BRAND.darkPanel }}
          data-testid="split-table"
        >
          {/* Table Header */}
          <div 
            className="grid px-3 py-3 font-semibold"
            style={{ backgroundColor: BRAND.darkRow, color: BRAND.white, fontSize: '13px', gridTemplateColumns: '2fr 1fr 1fr 1.2fr' }}
          >
            <span>Split Range</span>
            <span className="text-center">Time</span>
            <span className="text-right">km/h</span>
            <span className="text-right">Speed</span>
          </div>
          
          {/* Table Rows */}
          {splits.length > 0 ? (
            splits.map((split, index) => (
              <div
                key={index}
                className="grid px-3 py-3"
                style={{ 
                  backgroundColor: index % 2 === 0 ? BRAND.black : BRAND.darkRow,
                  color: BRAND.white,
                  fontSize: '13px',
                  gridTemplateColumns: '2fr 1fr 1fr 1.2fr'
                }}
                data-testid={`split-row-${index}`}
              >
                <span>{split.label}</span>
                <span className="text-center" style={{ color: BRAND.lightGray }}>
                  {formatSplitTime(split.elapsedTime || 0)}
                </span>
                <span className="text-right" style={{ color: BRAND.white }}>
                  {(split.speed * 3.6).toFixed(1)}
                </span>
                <span className="text-right" style={{ color: BRAND.orange }}>
                  {formatSpeed(split.speed)} m/s
                </span>
              </div>
            ))
          ) : (
            <div 
              className="px-4 py-6 text-center"
              style={{ color: BRAND.mediumGray, fontSize: '14px' }}
            >
              No split data available for this session.
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        {/* View History Button - Primary */}
        <button
          type="button"
          onClick={handleViewHistory}
          className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-98"
          style={{ 
            backgroundColor: BRAND.red,
            color: BRAND.white,
            fontSize: '18px'
          }}
          data-testid="view-history-button"
        >
          View History
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* New Session Button - Secondary */}
        <button
          type="button"
          onClick={handleNewSession}
          className="w-full py-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all active:scale-98 border-2"
          style={{ 
            backgroundColor: 'transparent',
            color: BRAND.white,
            borderColor: BRAND.red,
            fontSize: '18px'
          }}
          data-testid="new-session-button"
        >
          Start New Session
        </button>
      </div>
    </div>
  );
};

export default SessionSummaryPage_DBPRO;
